import type { InputRef, SelectProps } from "antd";
import { Button, Input, Popover, Tooltip } from "antd";
import { TooltipPlacement } from "antd/es/tooltip";
import { Check, ChevronDown } from "lucide-react";
import type { ReactNode } from "react";
import { useEffect, useMemo, useRef, useState } from "react";

import { Flex } from "antd";
import styles from "./styles.module.scss";

export type { SelectProps };

export type SelectButtonProps = Omit<SelectProps, "variant"> & {
  /**
   * When true, shows search input inside the dropdown.
   */
  searchable?: boolean;
  /**
   * When true (and mode is multiple/tags), shows footer actions with draft state.
   */
  withFooterActions?: boolean;
  applyText?: string;
  clearText?: string;
  /**
   * Placeholder text for the search input.
   */
  searchPlaceholder?: string;
  /**
   * Label text to display on the button when no value is selected.
   */
  label?: string;
  /**
   * Variant style for the button.
   */
  variant?: "default" | "primary";
  /**
   * Controlled search value. Parent component should filter options based on this.
   */
  searchValue?: string;
  /**
   * Callback when search value changes. Parent should update searchValue and filter options.
   */
  onSearchChange?: (value: string) => void;
  /**
   * Icon to display before the button text.
   */
  prefixIcon?: ReactNode;
  /**
   * Icon to display after the button text (before the chevron).
   */
  suffixIcon?: ReactNode;
  popOverPosition?: TooltipPlacement;

  isError?: boolean;
  isLoading?: boolean;
};

export function SelectButton(props: SelectButtonProps) {
  const {
    searchable = false,
    withFooterActions = false,
    applyText = "Select",
    clearText = "Clear ",
    searchPlaceholder = "Search...",
    label = "Select...",
    variant,
    size,
    onDropdownVisibleChange,
    onChange,
    value,
    mode,
    options,
    placeholder,
    searchValue,
    onSearchChange,
    suffixIcon,
    prefixIcon,
    popOverPosition = "bottomLeft",
    isError,
    isLoading,
  } = props;

  const isMulti = mode === "multiple" || mode === "tags";
  const enableFooter = withFooterActions && isMulti;
  const searchInputRef = useRef<InputRef>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const [open, setOpen] = useState<boolean>(false);
  const [draft, setDraft] = useState<any[]>(
    Array.isArray(value) ? (value as any[]) : []
  );
  const [maxChars, setMaxChars] = useState<number>(20);

  // Calculate max characters based on button width
  useEffect(() => {
    const calculateMaxChars = () => {
      const button = buttonRef.current;
      if (
        !button ||
        typeof (button as HTMLElement).querySelector !== "function"
      ) {
        return;
      }

      const buttonWidth = (button as HTMLElement).offsetWidth;
      const buttonTextElement = (button as HTMLElement).querySelector(
        `.${styles.buttonText}`
      ) as HTMLElement | null;

      if (!buttonTextElement) return;

      // Get computed styles
      const computedStyles = window.getComputedStyle(buttonTextElement);
      const fontSize = parseFloat(computedStyles.fontSize);
      const paddingLeft = parseFloat(computedStyles.paddingLeft) || 0;
      const paddingRight = parseFloat(computedStyles.paddingRight) || 0;
      const padding = paddingLeft + paddingRight;

      // Approximate character width (most fonts: ~0.6 * fontSize for average char)
      // Using a more conservative estimate
      const avgCharWidth = fontSize * 0.6;

      // Available width: button width - padding - chevron space (approx 30px)
      const availableWidth = buttonWidth - padding - 30;
      const calculatedMaxChars = Math.floor(availableWidth / avgCharWidth);

      // Set minimum of 10 and maximum reasonable limit
      setMaxChars(Math.max(10, Math.min(calculatedMaxChars, 200)));
    };

    calculateMaxChars();

    // Recalculate on resize
    const resizeObserver = new ResizeObserver(() => {
      calculateMaxChars();
    });

    const buttonEl = buttonRef.current;
    if (buttonEl && buttonEl instanceof HTMLElement) {
      resizeObserver.observe(buttonEl);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // Sync draft when value changes or when opening
  useEffect(() => {
    if (!enableFooter) return;
    if (open) {
      setDraft(Array.isArray(value) ? (value as any[]) : []);
    }
  }, [enableFooter, open, value]);

  // Focus search input when dropdown opens and searchable is true
  useEffect(() => {
    if (open && searchable && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.input?.focus();
      }, 100);
    }
  }, [open, searchable]);

  // Handle open state changes
  useEffect(() => {
    onDropdownVisibleChange?.(open);
  }, [open, onDropdownVisibleChange]);

  const optionsMap = useMemo(() => {
    const map = new Map<any, any>();
    (options || []).forEach((opt) => {
      if ("value" in opt) {
        map.set(opt.value, opt);
      }
    });
    return map;
  }, [options]);

  const displayOptions = useMemo(() => {
    return [
      ...(draft?.map((v) => {
        const opt = optionsMap.get(v);
        return {
          label: opt?.label ?? v,
          value: v,
        };
      }) ?? []),
      ...(options ?? []).filter((opt) => !draft.includes(opt.value)),
    ];
  }, [options, draft, optionsMap]);

  const handleInternalChange: SelectProps["onChange"] = (vals, option) => {
    if (enableFooter) {
      setDraft(vals as any[]);
    } else {
      onChange?.(vals, option);
      if (!isMulti) {
        setOpen(false);
      }
    }
  };

  const handleApply = () => {
    const optionList = draft.map((v) => optionsMap.get(v)).filter(Boolean);
    onChange?.(draft as any, optionList as any);
    setOpen(false);
  };

  const handleClear = () => {
    setDraft([]);
    onChange?.([], []);
    setOpen(false);
  };

  // Get selected option labels for multi-select (always resolve value → label via options)
  const getSelectedLabels = useMemo(() => {
    if (!isMulti || !value || !Array.isArray(value) || value.length === 0) {
      return [];
    }

    return (value as any[])
      .map((val) => {
        const option = (options || []).find((opt) => opt.value === val);
        if (!option) return typeof val === "string" ? val : String(val || "");
        return typeof option.label === "string"
          ? option.label
          : String(option.label || "");
      })
      .filter(Boolean) as string[];
  }, [isMulti, value, options]);

  // Get display text for button
  const getDisplayText = () => {
    if (!value || (Array.isArray(value) && value.length === 0)) {
      return placeholder || label;
    }

    if (isMulti) {
      if (getSelectedLabels.length === 0) return placeholder || label;

      const joinedLabels = getSelectedLabels.join(", ");
      // Trim based on calculated max characters
      if (joinedLabels.length > maxChars) {
        return joinedLabels.substring(0, maxChars) + "...";
      }
      return joinedLabels;
    } else {
      const selectedOption = (options || []).find((opt) => opt.value === value);
      return selectedOption
        ? typeof selectedOption.label === "string"
          ? selectedOption.label
          : String(selectedOption.label || "")
        : (value !== null && value !== undefined && value !== ""
            ? (typeof value === "string" ? value : String(value))
            : placeholder || label);
    }
  };

  // Get full tooltip text for multi-select
  const getTooltipText = () => {
    if (!isMulti || getSelectedLabels.length === 0) return undefined;
    const fullText = getSelectedLabels.join(", ");
    const displayText = getDisplayText();
    // Only show tooltip if text was trimmed
    if (
      fullText.length > maxChars &&
      typeof displayText === "string" &&
      displayText.endsWith("...")
    ) {
      return fullText;
    }
    return undefined;
  };

  const hasActiveValue = isMulti
    ? Array.isArray(value) && value.length > 0
    : value !== null && value !== undefined && value !== "";

  // Button styles based on variant and state
  const getButtonClassName = () => {
    const baseStyles = styles.selectButton;
    let variantStyles = "";
    let sizeStyles = "";

    const currentVariant: "default" | "primary" = (variant ?? "default") as
      | "default"
      | "primary";
    if (currentVariant === "primary") {
      variantStyles = styles.selectButtonPrimary;
    } else if (hasActiveValue) {
      variantStyles = styles.selectButtonActive;
    } else {
      variantStyles = styles.selectButtonDefault;
    }

    // Apply size styles
    if (size === "small") {
      sizeStyles = styles.selectButtonSmall;
    } else if (size === "large") {
      sizeStyles = styles.selectButtonLarge;
    } else {
      // Default to middle
      sizeStyles = styles.selectButtonMiddle;
    }

    return `${baseStyles} ${variantStyles} ${sizeStyles} ${
      isError ? styles.selectButtonError : ""
    }`;
  };

  const getChevronClassName = () => {
    const currentVariant: "default" | "primary" = (variant ?? "default") as
      | "default"
      | "primary";
    if (currentVariant === "primary") {
      return styles.chevronPrimary;
    }
    if (hasActiveValue) {
      return styles.chevronActive;
    }
    return styles.chevronDefault;
  };

  // Render custom options (used for both searchable and non-searchable)
  const renderCustomOptions = () => {
    const currentValue = enableFooter ? (open ? draft : value) : value;
    const isSelected = (val: any) => {
      if (isMulti) {
        return Array.isArray(currentValue) && currentValue.includes(val);
      }
      return currentValue === val;
    };

    const handleOptionClick = (opt: any) => {
      if (isMulti) {
        const current = Array.isArray(currentValue) ? currentValue : [];
        const newValue = current.includes(opt.value)
          ? current.filter((v) => v !== opt.value)
          : [...current, opt.value];
        handleInternalChange(newValue as any, [opt]);
      } else {
        handleInternalChange(opt.value, opt);
        if (!enableFooter) {
          setOpen(false);
        }
      }
    };

    if (isLoading) {
      return (
        <Flex justify="center" align="center" className={styles.loading}>
          Loading...
        </Flex>
      );
    }
    if (displayOptions.length === 0) {
      return <div className={styles.noResults}>No results found.</div>;
    }

    return (
      <div className={styles.optionsList}>
        {displayOptions.map((opt) => {
          const selected = isSelected(opt.value);
          return (
            <div
              key={opt.value}
              onClick={() => handleOptionClick(opt)}
              className={`${styles.optionItem} ${
                selected ? styles.optionItemSelected : ""
              }`}
            >
              <span className={styles.optionLabel}>
                {typeof opt.label === "string"
                  ? opt.label
                  : String(opt.label || "")}
              </span>
              {selected && <Check size={14} className={styles.checkIcon} />}
            </div>
          );
        })}
      </div>
    );
  };

  // Render search input section
  const renderSearchInput = () => {
    if (!searchable) return null;

    return (
      <div className={styles.searchContainer}>
        <div className={styles.searchWrapper}>
          {/* <Search size={14} className={styles.searchIcon} /> */}
          <Input
            ref={searchInputRef}
            value={searchValue || ""}
            onChange={(e) => onSearchChange?.(e.target.value)}
            placeholder={searchPlaceholder}
            className={styles.searchInput}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          />
        </div>
      </div>
    );
  };

  // Render footer actions for multiple selection
  const renderFooter = () => {
    if (!enableFooter) return null;

    return (
      <div className={styles.dropdownFooter}>
        <Button type="text" onClick={handleClear} className={styles.textButton}>
          {clearText}
        </Button>
        <Button
          type="primary"
          onClick={handleApply}
          className={styles.primaryButton}
        >
          {applyText}
        </Button>
      </div>
    );
  };

  // Render the complete dropdown panel
  const renderDropdownPanel = () => {
    return (
      <div
        className={styles.searchableDropdown}
        style={{
          width: "200px",
        }}
      >
        <div className={styles.dropdownPanel}>
          {renderSearchInput()}
          <div className={styles.dropdownBody}>{renderCustomOptions()}</div>
          {renderFooter()}
        </div>
      </div>
    );
  };

  // Render the button trigger
  const renderButton = () => {
    const displayText = getDisplayText();
    const tooltipText = getTooltipText();
    const buttonContent = (
      <button ref={buttonRef} type="button" className={getButtonClassName()}>
        {prefixIcon && <span className={styles.prefixIcon}>{prefixIcon}</span>}
        <span className={styles.buttonText}>{displayText}</span>
        {suffixIcon && <span className={styles.suffixIcon}>{suffixIcon}</span>}
        <ChevronDown
          size={14}
          className={`${styles.chevron} ${getChevronClassName()} ${
            open ? styles.chevronOpen : ""
          }`}
        />
      </button>
    );

    // Wrap with tooltip if multi-select and text is trimmed
    if (tooltipText) {
      return <Tooltip title={tooltipText}>{buttonContent}</Tooltip>;
    }

    return buttonContent;
  };

  return (
    <div className={styles.selectButtonWrapper}>
      <Popover
        content={renderDropdownPanel()}
        trigger="click"
        open={open}
        onOpenChange={setOpen}
        placement={popOverPosition}
        overlayClassName={styles.popoverOverlay}
        getPopupContainer={() => document.body}
        destroyTooltipOnHide={false}
        autoAdjustOverflow={false}
        zIndex={1050}
      >
        {renderButton()}
      </Popover>
    </div>
  );
}
